import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Pencil, Phone, Mail, MessageCircle, User, Stethoscope,
  CalendarClock, Banknote, FileText, Clock, UserCog, Flag, CircleDot,
  UserPlus, CalendarPlus, ExternalLink, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatApiErrorDetail } from "@/lib/api";import {
  LEAD_STATUSES, LEAD_PRIORITIES, LOST_REASONS, statusMeta, priorityMeta, initials,
} from "@/config/leads";
import { LeadFormDialog } from "@/components/leads/LeadFormDialog";
import { AppointmentFormDialog } from "@/components/appointments/AppointmentFormDialog";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

const activityIcon = { created: CircleDot, status: Flag, priority: Flag, owner: UserCog, note: FileText };

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm text-slate-800 break-words">{value || "—"}</p>
    </div>
  </div>
);

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState(LOST_REASONS[0]);
  const [converting, setConverting] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const [apptPatientId, setApptPatientId] = useState(null);

  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/leads/${id}/convert`);
      toast.success(data.created ? "Patient created from lead" : "Lead linked to existing patient");
      await load();
      return data.patient_id;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Conversion failed");
      return null;
    } finally {
      setConverting(false);
    }
  };

  const bookAppointment = async () => {
    let pid = lead?.patient_id;
    if (!pid) pid = await convert();
    if (!pid) return;
    setApptPatientId(pid);
    setApptOpen(true);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
    } catch {
      toast.error("Lead not found");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
    api.get("/leads/owners").then(({ data }) => setOwners(data.owners)).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!loading && searchParams.get("edit") === "1") {
      setEditOpen(true);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading, searchParams, setSearchParams]);

  const patch = async (updates) => {
    try {
      const { data } = await api.put(`/leads/${id}`, updates);
      setLead(data);
      toast.success("Lead updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const onStatusChange = (value) => {
    if (value === "lost") { setLostReason(LOST_REASONS[0]); setLostOpen(true); }
    else patch({ status: value });
  };

  const confirmLost = () => { setLostOpen(false); patch({ status: "lost", lost_reason: lostReason }); };

  if (loading || !lead) {
    return (
      <div className="space-y-6" data-testid="lead-detail-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-96 lg:col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const st = statusMeta(lead.status);
  const pr = priorityMeta(lead.priority);
  const activities = [...(lead.activities || [])].reverse();

  return (
    <div className="animate-fade-up space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate("/leads")} data-testid="back-to-leads"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900" data-testid="lead-detail-name">{lead.full_name}</h1>
              <Badge className={cn(st.badge)} data-testid="lead-detail-status-badge">{st.label}</Badge>
              <Badge className={cn("capitalize", pr.badge)}>{pr.label}</Badge>
              {typeof lead.ai_score === "number" && (
                <Badge variant="secondary" className="bg-slate-100 font-mono text-slate-600" title={lead.ai_explanation} data-testid="lead-ai-score">Score {lead.ai_score}</Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-500">{lead.interest} · {lead.source}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {lead.patient_id ? (
            <Button variant="outline" onClick={() => navigate(`/patients/${lead.patient_id}`)} data-testid="view-patient-button">
              <ExternalLink className="h-4 w-4" /> View Patient
            </Button>
          ) : (
            <Button variant="outline" onClick={convert} disabled={converting} data-testid="convert-lead-button">
              {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Convert to Patient
            </Button>
          )}
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-lead-button"><Pencil className="h-4 w-4" /> Edit</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={bookAppointment} disabled={converting} data-testid="book-appointment-button">
            <CalendarPlus className="h-4 w-4" /> Book Appointment
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: info + activity */}
        <div className="space-y-6 lg:col-span-2">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Contact & Service</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              <InfoRow icon={Phone} label="Phone" value={lead.phone} />
              <InfoRow icon={MessageCircle} label="WhatsApp" value={lead.whatsapp} />
              <InfoRow icon={Mail} label="Email" value={lead.email} />
              <InfoRow icon={User} label="Gender / Age" value={[lead.gender, lead.age].filter(Boolean).join(" · ")} />
              <InfoRow icon={Stethoscope} label="Interest / Service" value={lead.interest} />
              <InfoRow icon={Banknote} label="Expected Revenue" value={lead.expected_revenue ? `₹${lead.expected_revenue.toLocaleString()}` : null} />
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Source & Campaign</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              <InfoRow icon={Flag} label="Source" value={lead.source} />
              <InfoRow icon={FileText} label="Campaign" value={lead.campaign} />
              <InfoRow icon={FileText} label="Medium" value={lead.medium} />
              <InfoRow icon={FileText} label="Keyword" value={lead.keyword} />
              <InfoRow icon={FileText} label="Landing Page" value={lead.landing_page} />
              {lead.status === "lost" && <InfoRow icon={Flag} label="Lost Reason" value={lead.lost_reason} />}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Preferred Appointment & Notes</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                <InfoRow icon={Stethoscope} label="Preferred Doctor" value={lead.preferred_doctor} />
                <InfoRow icon={CalendarClock} label="Preferred Date / Time" value={[lead.preferred_date, lead.preferred_time].filter(Boolean).join(" · ")} />
              </div>
              <Separator className="my-3" />
              <div className="flex items-start gap-3">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <p className="whitespace-pre-wrap text-sm text-slate-700" data-testid="lead-notes">{lead.notes || "No notes yet."}</p>
              </div>
            </CardContent>
          </Card>

          {/* Activity timeline */}
          <Card className="shadow-sm" data-testid="lead-activity">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Activity</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activities.map((a, i) => {
                  const Icon = activityIcon[a.type] || Clock;
                  return (
                    <div key={i} className="flex gap-3" data-testid="activity-item">
                      <div className="flex flex-col items-center">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><Icon className="h-4 w-4" /></div>
                        {i < activities.length - 1 && <div className="my-1 w-px flex-1 bg-slate-200" />}
                      </div>
                      <div className="pb-1">
                        <p className="text-sm text-slate-800">{a.message}</p>
                        <p className="text-xs text-slate-400">{fmt(a.at)}{a.by ? ` · ${a.by}` : ""}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: quick controls */}
        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Lead Progress</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-wider text-slate-400">Status</label>
                <Select value={lead.status} onValueChange={onStatusChange}>
                  <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-wider text-slate-400">Priority</label>
                <Select value={lead.priority} onValueChange={(v) => patch({ priority: v })}>
                  <SelectTrigger data-testid="detail-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-wider text-slate-400">Assigned Owner</label>
                <Select value={lead.owner_id || "unassigned"} onValueChange={(v) => patch({ owner_id: v === "unassigned" ? null : v })}>
                  <SelectTrigger data-testid="detail-owner-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-3 pt-6">
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9"><AvatarImage src={lead.owner_avatar} /><AvatarFallback className="bg-emerald-100 text-xs text-emerald-700">{initials(lead.owner_name || "NA")}</AvatarFallback></Avatar>
                <div>
                  <p className="text-xs text-slate-400">Owner</p>
                  <p className="text-sm font-medium text-slate-800">{lead.owner_name || "Unassigned"}</p>
                </div>
              </div>
              <Separator />
              <p className="text-xs text-slate-400">Created</p>
              <p className="text-sm text-slate-700">{fmt(lead.created_at)}</p>
              <p className="text-xs text-slate-400">Last updated</p>
              <p className="text-sm text-slate-700">{fmt(lead.updated_at)}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <LeadFormDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} owners={owners} onSaved={load} />

      <AppointmentFormDialog
        open={apptOpen}
        onOpenChange={setApptOpen}
        presetPatientId={apptPatientId}
        presetPatientName={lead.full_name}
        presetLeadId={lead.id}
        onBooked={load}
      />

      {/* Lost reason dialog */}
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lost-reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark lead as Lost</DialogTitle>
            <DialogDescription>Optionally record why this lead was lost. This helps future analytics.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger data-testid="lost-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LOST_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmLost} data-testid="confirm-lost">Mark as Lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
